import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "bc_cart_v1";

function loadCart() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function saveCart(items) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
        /* ignore */
    }
}

export function CartProvider({ children }) {
    const [items, setItems] = useState(loadCart);

    useEffect(() => {
        saveCart(items);
    }, [items]);

    const addItem = (product, quantity = 1) => {
        setItems((prev) => {
            const key = product.id;
            const existing = prev.find((i) => i.id === key);
            if (existing) {
                return prev.map((i) =>
                    i.id === key ? { ...i, quantity: +(i.quantity + quantity).toFixed(2) } : i,
                );
            }
            return [
                ...prev,
                {
                    id: product.id,
                    code: product.code || "",
                    article: product.article,
                    prix_kg_ht: product.prix_kg_ht,
                    unite: product.unite || "kg",
                    quantity,
                },
            ];
        });
        toast.success(`${product.article} ajouté au panier`);
    };

    const removeItem = (id) => {
        setItems((prev) => prev.filter((i) => i.id !== id));
    };

    const updateQuantity = (id, quantity) => {
        const q = Math.max(0, Number(quantity) || 0);
        if (q === 0) return removeItem(id);
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: q } : i)));
    };

    const clear = () => setItems([]);

    const totals = useMemo(() => {
        let total_ht = 0;
        let has_sur_demande = false;
        for (const it of items) {
            if (it.prix_kg_ht == null) has_sur_demande = true;
            else total_ht += Number(it.quantity) * Number(it.prix_kg_ht);
        }
        return {
            count: items.length,
            qty_sum: items.reduce((s, i) => s + Number(i.quantity), 0),
            total_ht: Math.round(total_ht * 100) / 100,
            has_sur_demande,
        };
    }, [items]);

    return (
        <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clear, totals }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    return useContext(CartContext);
}
